import type { ActiveChat } from '@/app/(protected)/[workspace_slug]/chat/ChatContext';
import type {
  ChatAuthorUser,
  ChatConversation,
  ChatConversationType,
} from '@/features/chat/types';

const BUILT_IN_ACTIVE_CHAT_TYPES = new Set<ChatConversationType>([
  'stream',
  'general_channel',
  'unread',
  'mentions',
]);

export function getChatUserDisplayName(user: ChatAuthorUser) {
  return (
    user.display_name ||
    `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
    user.email ||
    'Unknown member'
  );
}

export function getDirectMessageLabel(
  destination: ChatConversation,
  currentUserEmail?: string | null,
) {
  const currentEmail = currentUserEmail?.trim().toLowerCase();
  const participants = destination.participants ?? [];
  const visibleParticipants = participants.filter(
    (participant) => participant.email?.trim().toLowerCase() !== currentEmail,
  );
  const participantsToRender = visibleParticipants.length
    ? visibleParticipants
    : participants;

  if (participantsToRender.length) {
    return participantsToRender.map(getChatUserDisplayName).join(', ');
  }

  return destination.title;
}

export function getChatDestinationLabel(
  destination: ChatConversation,
  currentUserEmail?: string | null,
) {
  if (destination.conversation_type === 'stream') {
    return 'Stream';
  }

  if (destination.conversation_type === 'general_channel') {
    return 'General';
  }

  if (destination.conversation_type === 'unread') {
    return 'Unread';
  }

  if (destination.conversation_type === 'mentions') {
    return 'Mentions';
  }

  if (destination.conversation_type === 'direct_message') {
    return getDirectMessageLabel(destination, currentUserEmail);
  }

  return destination.title;
}

export function getChatDestinationDescription(destination: ChatConversation) {
  if (destination.conversation_type === 'stream') {
    return 'Activity across visible conversations';
  }

  if (destination.conversation_type === 'general_channel') {
    return 'Workspace-wide coordination and operating notes';
  }

  if (destination.conversation_type === 'unread') {
    return 'Messages that need your attention';
  }

  if (destination.conversation_type === 'mentions') {
    return 'Messages where people mentioned you';
  }

  if (destination.conversation_type === 'job_channel') {
    return destination.job?.name
      ? `Job channel for ${destination.job.name}`
      : 'Job channel';
  }

  if (destination.conversation_type === 'direct_message') {
    return 'Direct message';
  }

  return undefined;
}

export function toActiveChat(
  destination: ChatConversation,
  currentUserEmail?: string | null,
): ActiveChat {
  return {
    id: destination.id,
    type: destination.conversation_type,
    title: getChatDestinationLabel(destination, currentUserEmail),
    description: getChatDestinationDescription(destination),
    jobId: destination.job?.id,
    isArchived: destination.is_archived,
    isReadOnly: destination.is_read_only,
  };
}

export function isActiveDestination(
  activeChat: ActiveChat,
  destination: ActiveChat,
) {
  if (
    BUILT_IN_ACTIVE_CHAT_TYPES.has(activeChat.type) &&
    BUILT_IN_ACTIVE_CHAT_TYPES.has(destination.type)
  ) {
    return activeChat.type === destination.type;
  }

  return (
    activeChat.id === destination.id && activeChat.type === destination.type
  );
}
